import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AddSalaryDetail from './AddSalaryDetail';
import EditSalaryDetail from './EditSalaryDetail';
import ImportSalaryDetails from './ImportSalaryDetails';

const SalaryDetails = () => {
  const [showImportModal, setShowImportModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [salaryDetails, setSalaryDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedSalaryDetail, setSelectedSalaryDetail] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [payComponents, setPayComponents] = useState([]);
  const [debugInfo, setDebugInfo] = useState('');
  const [userPermissions, setUserPermissions] = useState({
    can_view: false,
    can_add: false,
    can_edit: false,
    can_delete: false,
    loaded: false
  });

  // Salary calculation state
  const [salarySummaries, setSalarySummaries] = useState({});
  const [calculatingSalaries, setCalculatingSalaries] = useState(false);
  const [calculationProgress, setCalculationProgress] = useState(0);

  // NEW: State for detailed calculations modal
  const [showCalculationsModal, setShowCalculationsModal] = useState(false);
  const [detailedCalculations, setDetailedCalculations] = useState({});
  const [loadingCalculations, setLoadingCalculations] = useState(false);

  // Refs to prevent infinite loops
  const calculationInProgress = useRef(false);
  const lastCalculationTime = useRef(0);

  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

  // Debug function to check API responses
  const debugResponse = (apiName, response, error) => {
    console.log(`[DEBUG] ${apiName}:`, {
      success: response?.data?.success,
      data: response?.data,
      status: response?.status,
      error: error?.message
    });

    if (error) {
      setDebugInfo(prev => prev + `\n${apiName} Error: ${error.message}`);
    }
  };

  // Fetch user permissions
  const fetchUserPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          loaded: true
        });
        return;
      }

      const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      debugResponse('get_user_permissions.php', response);

      if (response.data.success && response.data.permissions) {
        const salaryDetailsPermissions = response.data.permissions.find(
          p => p.module_name === 'SalaryDetails' || p.module_name === 'EmployeeSalary'
        );

        if (salaryDetailsPermissions) {
          setUserPermissions({
            can_view: Boolean(salaryDetailsPermissions.can_view),
            can_add: Boolean(salaryDetailsPermissions.can_add),
            can_edit: Boolean(salaryDetailsPermissions.can_edit),
            can_delete: Boolean(salaryDetailsPermissions.can_delete),
            loaded: true
          });
        } else {
          // Default permissions if module not found
          setUserPermissions({
            can_view: true,
            can_add: false,
            can_edit: false,
            can_delete: false,
            loaded: true
          });
        }
      } else {
        // Default permissions on error
        setUserPermissions({
          can_view: true,
          can_add: true,
          can_edit: true,
          can_delete: true,
          loaded: true
        });
      }
    } catch (error) {
      debugResponse('get_user_permissions.php', null, error);
      // Default permissions on exception
      setUserPermissions({
        can_view: true,
        can_add: true,
        can_edit: true,
        can_delete: true,
        loaded: true
      });
    }
  }, [API_BASE]);
  const canImportSalaryDetails = () => userPermissions.can_add;
  const handleImport = async (records, progressCallback) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const totalRecords = records.length;
      let processed = 0;

      // Process in batches
      const batchSize = 50;
      for (let i = 0; i < records.length; i += batchSize) {
        const batch = records.slice(i, i + batchSize);

        const response = await axios.post(
          `${API_BASE}imports/import_salary_details.php`,
          { records: batch },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.data.success) {
          throw new Error(response.data.error || 'Import failed');
        }

        processed += batch.length;
        const progress = Math.round((processed / totalRecords) * 100);
        progressCallback(progress);
      }

      // Refresh data after import
      await fetchSalaryDetails();

      // Recalculate salaries for all employees
      const employeeIds = employees.map(emp => emp.emp_id);
      await calculateSalarySummaries(employeeIds);

    } catch (error) {
      console.error('[DEBUG] Import error:', error);
      throw error;
    }
  };

  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      console.log('[DEBUG] Fetching employees from:', `${API_BASE}/get_employees.php`);

      const response = await axios.get(`${API_BASE}/get_employees.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      debugResponse('get_employees.php', response);

      if (response.data && response.data.success) {
        setEmployees(response.data.employees || []);
      } else {
        setEmployees([]);
        console.warn('[DEBUG] No employees data or success flag false:', response.data);
      }
    } catch (error) {
      debugResponse('get_employees.php', null, error);
      setEmployees([]);
      console.error('[DEBUG] Error fetching employees:', error);
    }
  }, [API_BASE]);

  const fetchSalaryDetails = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      console.log('[DEBUG] Fetching salary details from:', `${API_BASE}/get_salary_details.php`);

      const response = await axios.get(`${API_BASE}/get_salary_details.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      debugResponse('get_salary_details.php', response);

      if (response.data && response.data.success) {
        setSalaryDetails(response.data.salary_details || []);
        setError('');
      } else {
        setSalaryDetails([]);
        const errorMsg = response.data?.error || 'Failed to fetch salary details';
        setError(errorMsg);
        console.warn('[DEBUG] Salary details fetch warning:', errorMsg);
      }
    } catch (error) {
      debugResponse('get_salary_details.php', null, error);
      setSalaryDetails([]);
      const errorMsg = error.response?.data?.error ||
        error.message ||
        'Failed to fetch salary details. Please check your connection.';
      setError(errorMsg);
      console.error('[DEBUG] Error fetching salary details:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  // Helper: Calculate simple salary (no formulas)
  const calculateSimpleSalary = useCallback((details) => {
    let earnings = 0;
    let deductions = 0;

    details.forEach(detail => {
      const value = parseFloat(detail.value) || 0;
      if (detail.component_type === 'Earning' || detail.component_type === 'Earing') {
        earnings += value;
      } else if (detail.component_type === 'Deduction') {
        deductions += value;
      }
    });

    return {
      earnings: earnings.toFixed(2),
      deductions: deductions.toFixed(2),
      net: (earnings - deductions).toFixed(2)
    };
  }, []);

  // Helper: Get salary details for a specific employee
  const getSalaryDetailsForEmployee = useCallback((empId) => {
    return salaryDetails.filter(detail => detail.emp_id === empId);
  }, [salaryDetails]);

  // Helper: Calculate simple summaries for a batch (fallback)
  const calculateSimpleSummariesForBatch = useCallback((employeeIds, targetObject) => {
    employeeIds.forEach(empId => {
      const details = getSalaryDetailsForEmployee(empId);
      const summary = calculateSimpleSalary(details);
      targetObject[empId] = summary;
    });
  }, [getSalaryDetailsForEmployee, calculateSimpleSalary]);

  // Helper: Calculate simple summaries for all employees (fallback)
  const calculateSimpleSummariesForAll = useCallback((employeeIds) => {
    const summaries = {};
    calculateSimpleSummariesForBatch(employeeIds, summaries);
    setSalarySummaries(summaries);
  }, [calculateSimpleSummariesForBatch]);

  // Get salary summary for employee
  const getSalarySummary = useCallback((empId) => {
    const summary = salarySummaries[empId];

    if (summary) {
      return {
        earnings: parseFloat(summary.earnings || 0).toFixed(2),
        deductions: parseFloat(summary.deductions || 0).toFixed(2),
        net: parseFloat(summary.net || 0).toFixed(2)
      };
    }

    // If not calculated yet, show calculating or default
    if (calculatingSalaries) {
      return {
        earnings: 'Calculating...',
        deductions: 'Calculating...',
        net: 'Calculating...'
      };
    }

    return {
      earnings: '0.00',
      deductions: '0.00',
      net: '0.00'
    };
  }, [salarySummaries, calculatingSalaries]);

  // MAIN FUNCTION: Calculate salary summaries using the calculate_salary_details.php API
  const calculateSalarySummaries = useCallback(async (employeeIds) => {
    // Prevent multiple simultaneous calculations
    if (calculationInProgress.current || !employeeIds || employeeIds.length === 0) {
      console.log('[DEBUG] Calculation skipped - already in progress or no employees');
      return;
    }

    calculationInProgress.current = true;
    setCalculatingSalaries(true);
    setCalculationProgress(0);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('[DEBUG] Starting salary calculation for', employeeIds.length, 'employees');

      // Split into smaller batches to avoid timeouts
      const batchSize = 10;
      const batches = [];

      for (let i = 0; i < employeeIds.length; i += batchSize) {
        batches.push(employeeIds.slice(i, i + batchSize));
      }

      const allSummaries = {};
      const allDetailedCalculations = {}; // Store detailed calculations

      // Process each batch
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        const empIdsString = batch.join(',');

        console.log(`[DEBUG] Calculating batch ${batchIndex + 1}/${batches.length} for employees:`, batch);

        try {
          // CALL THE calculate_salary_details.php API HERE
          const response = await axios.get(
            `${API_BASE}/calculate_salary_details.php?emp_ids=${empIdsString}&limit=${batchSize}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              timeout: 30000 // 30 second timeout for calculation
            }
          );

          debugResponse('calculate_salary_details.php', response);

          if (response.data && response.data.success && response.data.summaries) {
            // Merge the batch results
            Object.assign(allSummaries, response.data.summaries);

            // If we have detailed components in the response, store them
            if (response.data.components) {
              Object.assign(allDetailedCalculations, response.data.components);
            }

            console.log(`[DEBUG] Batch ${batchIndex + 1} successful, got`, Object.keys(response.data.summaries).length, 'summaries');
          } else {
            console.warn(`[DEBUG] Batch ${batchIndex + 1} failed or no data:`, response.data);
            // Calculate simple summaries for failed batch
            calculateSimpleSummariesForBatch(batch, allSummaries);
          }

        } catch (batchError) {
          console.error(`[DEBUG] Batch ${batchIndex + 1} API error:`, batchError);
          // Calculate simple summaries for failed batch
          calculateSimpleSummariesForBatch(batch, allSummaries);
        }

        // Update progress
        const progress = Math.round(((batchIndex + 1) / batches.length) * 100);
        setCalculationProgress(progress);

        // Small delay between batches to avoid overwhelming the server
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }

      // Update state with all calculated summaries
      setSalarySummaries(allSummaries);
      setDetailedCalculations(allDetailedCalculations);
      lastCalculationTime.current = Date.now();

      console.log('[DEBUG] Salary calculation completed. Total summaries:', Object.keys(allSummaries).length);

    } catch (error) {
      console.error('[DEBUG] Fatal error in salary calculation:', error);
      // Fallback to simple calculation for all employees
      calculateSimpleSummariesForAll(employeeIds);
    } finally {
      setCalculatingSalaries(false);
      setCalculationProgress(0);
      calculationInProgress.current = false;
    }
  }, [API_BASE, calculateSimpleSummariesForBatch, calculateSimpleSummariesForAll]);

  // NEW FUNCTION: Fetch detailed calculations for all employees
  const fetchDetailedCalculations = useCallback(async () => {
    if (employees.length === 0 || loadingCalculations) {
      return;
    }

    setLoadingCalculations(true);
    setDetailedCalculations({});

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token');
      }

      const employeeIds = employees.map(emp => emp.emp_id);
      const allCalculations = {};

      // Fetch detailed calculations for each employee
      for (let i = 0; i < employeeIds.length; i++) {
        const empId = employeeIds[i];

        try {
          const response = await axios.post(
            `${API_BASE}/calculate_salary_details.php`,
            { emp_id: empId },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              timeout: 15000
            }
          );

          if (response.data && response.data.success) {
            allCalculations[empId] = {
              summary: response.data.salary_summary,
              components: response.data.components || []
            };
          }
        } catch (error) {
          console.error(`[DEBUG] Error fetching details for employee ${empId}:`, error);
          allCalculations[empId] = {
            summary: getSalarySummary(empId),
            components: [],
            error: 'Failed to load detailed calculation'
          };
        }

        // Update progress
        const progress = Math.round(((i + 1) / employeeIds.length) * 100);
        setCalculationProgress(progress);
      }

      setDetailedCalculations(allCalculations);
      setShowCalculationsModal(true);

    } catch (error) {
      console.error('[DEBUG] Error fetching detailed calculations:', error);
      setError('Failed to load detailed calculations');
    } finally {
      setLoadingCalculations(false);
      setCalculationProgress(0);
    }
  }, [employees, API_BASE, loadingCalculations, getSalarySummary]);

  // Refresh salary summaries with cooldown protection
  const refreshSalarySummaries = useCallback(async () => {
    const now = Date.now();
    const timeSinceLastCalc = now - lastCalculationTime.current;

    // Prevent too frequent calculations (minimum 3 seconds between)
    if (timeSinceLastCalc < 3000) {
      console.log('[DEBUG] Calculation cooldown active, skipping');
      return;
    }

    if (employees.length === 0 || calculationInProgress.current) {
      console.log('[DEBUG] No employees or calculation already in progress');
      return;
    }

    const employeeIds = employees.map(emp => emp.emp_id);
    console.log('[DEBUG] Refreshing salary summaries for', employeeIds.length, 'employees');
    await calculateSalarySummaries(employeeIds);
  }, [employees, calculateSalarySummaries]);

  // Function to calculate salary for a single employee
  const calculateSingleEmployeeSalary = useCallback(async (empId) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token');
      }

      console.log('[DEBUG] Calculating salary for single employee:', empId);

      // Try POST method
      const response = await axios.post(
        `${API_BASE}/calculate_salary_details.php`,
        { emp_id: empId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );

      debugResponse('calculate_salary_details.php (single)', response);

      if (response.data && response.data.success && response.data.salary_summary) {
        // Update the specific employee's summary
        setSalarySummaries(prev => ({
          ...prev,
          [empId]: response.data.salary_summary
        }));

        // Update detailed calculations if available
        if (response.data.components) {
          setDetailedCalculations(prev => ({
            ...prev,
            [empId]: response.data.components
          }));
        }

        return response.data.salary_summary;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('[DEBUG] Error calculating single employee salary:', error);
      // Fallback to simple calculation
      const details = getSalaryDetailsForEmployee(empId);
      const summary = calculateSimpleSalary(details);

      setSalarySummaries(prev => ({
        ...prev,
        [empId]: summary
      }));

      return summary;
    }
  }, [API_BASE, getSalaryDetailsForEmployee, calculateSimpleSalary]);



  const handleCloneSalary = (employee) => {
    if (!canEditSalaryDetail()) {
      alert('You do not have permission to clone salary components');
      return;
    }

    navigate('/salary-clone', {
      state: {
        sourceEmployee: employee,
        sourceSalaryDetails: getSalaryDetailsForEmployee(employee.emp_id)
      }
    });
  };

  // NEW: View detailed calculations for a specific employee
  const viewEmployeeCalculations = useCallback(async (employee) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('Please login to view calculations');
        return;
      }

      setLoadingCalculations(true);

      const response = await axios.post(
        `${API_BASE}/calculate_salary_details.php`,
        { emp_id: employee.emp_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 15000
        }
      );

      if (response.data && response.data.success) {
        setDetailedCalculations({
          [employee.emp_id]: {
            employee: employee,
            summary: response.data.salary_summary,
            components: response.data.components || []
          }
        });
        setShowCalculationsModal(true);
      } else {
        alert('Failed to load calculation details');
      }
    } catch (error) {
      console.error('[DEBUG] Error loading calculation details:', error);
      alert('Error loading calculation details');
    } finally {
      setLoadingCalculations(false);
    }
  }, [API_BASE]);

  const fetchPayComponents = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.warn('[DEBUG] No token for pay components');
        return;
      }

      console.log('[DEBUG] Fetching pay components from:', `${API_BASE}/pay_components.php`);

      const response = await axios.get(`${API_BASE}/pay_components.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      debugResponse('pay_components.php', response);

      if (response.data && response.data.success) {
        setPayComponents(response.data.pay_components?.filter(comp => comp.is_active) || []);
      } else {
        setPayComponents([]);
        console.warn('[DEBUG] No pay components data:', response.data);
      }
    } catch (error) {
      debugResponse('pay_components.php', null, error);
      setPayComponents([]);
      console.error('[DEBUG] Error fetching pay components:', error);
    }
  }, [API_BASE]);

  // Initialize all data
  useEffect(() => {
    let mounted = true;

    const initializeData = async () => {
      setDebugInfo('Initializing data...');
      try {
        await fetchUserPermissions();
        await Promise.all([
          fetchEmployees(),
          fetchSalaryDetails(),
          fetchPayComponents()
        ]);

        if (mounted) {
          setDebugInfo('Data loaded successfully');
        }
      } catch (error) {
        console.error('[DEBUG] Initialization error:', error);
        if (mounted) {
          setError(`Initialization failed: ${error.message}`);
        }
      }
    };

    initializeData();

    return () => {
      mounted = false;
    };
  }, [fetchEmployees, fetchSalaryDetails, fetchPayComponents, fetchUserPermissions]);

  // Calculate salaries when employees and salary details are loaded
  useEffect(() => {
    if (employees.length > 0 && salaryDetails.length > 0 && !calculationInProgress.current) {
      const employeeIds = employees.map(emp => emp.emp_id);

      // Check if we need to calculate (not calculated recently or never calculated)
      const now = Date.now();
      const shouldCalculate = (
        now - lastCalculationTime.current > 5000 || // 5 seconds cooldown
        Object.keys(salarySummaries).length === 0 // Never calculated
      );

      if (shouldCalculate) {
        console.log('[DEBUG] Triggering salary calculation for', employeeIds.length, 'employees');
        calculateSalarySummaries(employeeIds);
      } else {
        console.log('[DEBUG] Skipping calculation - recent calculation exists');
      }
    }
  }, [employees, salaryDetails, calculateSalarySummaries, salarySummaries]);

  // Permission check functions
  const canViewSalaryDetails = () => userPermissions.can_view;
  const canAddSalaryDetail = () => userPermissions.can_add;
  const canEditSalaryDetail = () => userPermissions.can_edit;
  const canDeleteSalaryDetail = () => userPermissions.can_delete;

  const handleAdd = (employee) => {
    if (!canAddSalaryDetail()) {
      alert('You do not have permission to add salary components');
      return;
    }
    setSelectedEmployee(employee);
    setShowAddModal(true);
  };

  const handleEdit = (salaryDetail, employee) => {
    if (!canEditSalaryDetail()) {
      alert('You do not have permission to edit salary components');
      return;
    }
    setSelectedSalaryDetail(salaryDetail);
    setSelectedEmployee(employee);
    setShowEditModal(true);
  };

  const handleDelete = async (salaryDetailId, empId) => {
    if (!canDeleteSalaryDetail()) {
      alert('You do not have permission to delete salary components');
      return;
    }

    if (window.confirm('Are you sure you want to delete this salary component?')) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.post(`${API_BASE}/delete_salary_detail.php`,
          { salary_detail_id: salaryDetailId, emp_id: empId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        debugResponse('delete_salary_detail.php', response);

        if (response.data.success) {
          // Refresh salary details
          await fetchSalaryDetails();
          // Recalculate salary for this specific employee
          await calculateSingleEmployeeSalary(empId);
          alert('Salary component deleted successfully');
        } else {
          setError(response.data.error || 'Failed to delete salary component');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Failed to delete salary component';
        setError(errorMsg);
        console.error('[DEBUG] Error deleting salary component:', error);
      }
    }
  };





  const filteredEmployees = employees.filter(employee => {
    const matchesSearch =
      employee.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.emp_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'All' || employee.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.first_name.localeCompare(b.first_name);
      case 'code':
        return a.emp_code.localeCompare(b.emp_code);
      case 'status':
        return a.status.localeCompare(b.status);
      default:
        return a.emp_id - b.emp_id;
    }
  });

  const getComponentName = (componentId) => {
    const component = payComponents.find(pc => pc.component_id === componentId);
    return component?.component_name || `Component ${componentId}`;
  };

  // Loading state
  if ((loading && employees.length === 0) || !userPermissions.loaded) {
    return (
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading salary details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Permission denied state
  if (!canViewSalaryDetails()) {
    return (
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center py-5">
                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                <h3 className="mt-3 text-warning">Access Denied</h3>
                <p className="text-muted">You do not have permission to view salary details.</p>
                <button
                  className="btn btn-primary mt-3"
                  onClick={() => navigate('/dashboard')}
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body py-4">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="btn-group me-3" role="group">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate('/admin-panel')}
                        title="Go to Dashboard"
                      >
                        <i className="bi bi-speedometer2"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate('/Menu')}
                        title="Go to Menu"
                      >
                        <i className="bi bi-menu-button"></i>
                      </button>
                    </div>
                    <div>
                      <h2 className="h4 mb-1 fw-bold text-dark">
                        <i className="bi bi-cash-stack me-2 text-primary"></i>
                        Salary Settings
                        {!canAddSalaryDetail() && !canEditSalaryDetail() && !canDeleteSalaryDetail() && (
                          <span className="badge bg-warning ms-2">View Only</span>
                        )}
                      </h2>
                      <p className="text-muted mb-0">
                        Manage salary components for employees without salary templates
                      </p>
                      <small className="text-muted">
                        API Base: {API_BASE} | Employees: {employees.length} | Components: {salaryDetails.length}
                        {calculatingSalaries && ` | Calculating: ${calculationProgress}%`}
                      </small>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <div className="d-flex justify-content-end align-items-center">
                    <button
                      className="btn btn-outline-warning me-2"
                      onClick={() => setShowImportModal(true)}
                      disabled={!canImportSalaryDetails()}
                      title="Import Salary Deductions"
                    >
                      <i className="bi bi-upload me-2"></i>
                      Import
                    </button>
                    {/* NEW: View Calculations Button */}
                    <button
                      className="btn btn-outline-info me-2"
                      onClick={fetchDetailedCalculations}
                      disabled={loadingCalculations || calculatingSalaries || employees.length === 0}
                      title="View All Salary Calculations"
                    >
                      {loadingCalculations ? (
                        <i className="bi bi-arrow-clockwise spinner-border spinner-border-sm me-2"></i>
                      ) : (
                        <i className="bi bi-calculator me-2"></i>
                      )}
                      View Calculations
                    </button>
                    <button
                      className="btn btn-outline-secondary me-2"
                      onClick={() => navigate('/dashboard')}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Back to Dashboard
                    </button>
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => navigate('/Menu')}
                    >
                      <i className="bi bi-file-earmark-text me-2"></i>
                      Menu
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Calculation Progress */}
      {(calculatingSalaries || loadingCalculations) && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-primary">
              <div className="card-body py-3">
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm text-primary me-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between mb-1">
                      <small className="text-primary">
                        {calculatingSalaries ? 'Calculating salaries...' : 'Loading detailed calculations...'}
                      </small>
                      <small className="text-primary fw-bold">{calculationProgress}%</small>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                        role="progressbar"
                        style={{ width: `${calculationProgress}%` }}
                        aria-valuenow={calculationProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Debug Panel (only in development) */}
      {process.env.NODE_ENV === 'development' && debugInfo && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-warning">
              <div className="card-header bg-warning text-dark">
                <i className="bi bi-bug me-2"></i>
                Debug Information
                <button
                  className="btn btn-sm btn-outline-dark float-end"
                  onClick={() => setDebugInfo('')}
                >
                  Clear
                </button>
              </div>
              <div className="card-body">
                <pre className="mb-0" style={{ fontSize: '12px', maxHeight: '200px', overflowY: 'auto' }}>
                  {debugInfo}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>
                <strong>Error:</strong> {error}
                <div className="mt-2">
                  <button
                    className="btn btn-sm btn-outline-danger me-2"
                    onClick={fetchSalaryDetails}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Retry
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setError('')}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="bi bi-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search employees..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-outline-secondary border-start-0"
                        onClick={() => setSearchTerm('')}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="All">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Resigned">Resigned</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="name">Sort by Name</option>
                    <option value="code">Sort by Employee Code</option>
                    <option value="status">Sort by Status</option>
                  </select>
                </div>
                <div className="col-md-2 text-end">
                  <button
                    className="btn btn-outline-secondary me-2"
                    onClick={fetchSalaryDetails}
                    title="Refresh"
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </button>
                  <button
                    className="btn btn-outline-success"
                    onClick={refreshSalarySummaries}
                    title="Recalculate All Salaries"
                    disabled={calculatingSalaries || loadingCalculations}
                  >
                    {calculatingSalaries ? (
                      <i className="bi bi-arrow-clockwise spinner-border spinner-border-sm"></i>
                    ) : (
                      <i className="bi bi-calculator"></i>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Employees List */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body p-0">
              {sortedEmployees.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-people display-1 text-muted"></i>
                  <h4 className="mt-3 text-muted">No employees found</h4>
                  <p className="text-muted">
                    {employees.length === 0
                      ? "No employees available. Please add employees first."
                      : "No employees match your search criteria."
                    }
                  </p>
                  {employees.length === 0 && (
                    <button
                      className="btn btn-primary mt-2"
                      onClick={() => navigate('/employees')}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Add Employees
                    </button>
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Employee</th>
                        <th>Contact</th>
                        <th>Status</th>
                        <th>Salary Summary</th>
                        <th className="text-end pe-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedEmployees.map((employee) => {
                        const salaryDetailsForEmp = getSalaryDetailsForEmployee(employee.emp_id);
                        const salarySummary = getSalarySummary(employee.emp_id);

                        return (
                          <React.Fragment key={employee.emp_id}>
                            {/* Employee Row */}
                            <tr className="employee-row">
                              <td className="ps-4">
                                <div className="d-flex align-items-center">
                                  <div className="employee-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                    style={{ width: '40px', height: '40px' }}
                                  >
                                    <i className="bi bi-person"></i>
                                  </div>
                                  <div>
                                    <div className="fw-semibold">
                                      {employee.first_name} {employee.last_name}
                                    </div>
                                    <small className="text-muted">
                                      ID: {employee.emp_id} | Code: {employee.emp_code}
                                    </small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div>
                                  <small className="text-muted d-block">{employee.email || 'No email'}</small>
                                  <small className="text-muted">{employee.phone || 'No phone'}</small>
                                </div>
                              </td>
                              <td>
                                <span className={`badge ${employee.status === 'Active' ? 'bg-success' :
                                  employee.status === 'Inactive' ? 'bg-secondary' :
                                    'bg-danger'
                                  }`}>
                                  {employee.status || 'Unknown'}
                                </span>
                              </td>
                              <td>
                                <div>
                                  <small className="text-muted d-block">
                                    Earnings: ₹{salarySummary.earnings}
                                  </small>
                                  <small className="text-muted d-block">
                                    Deductions: ₹{salarySummary.deductions}
                                  </small>
                                  <small className="fw-bold text-primary">
                                    Net: ₹{salarySummary.net}
                                  </small>
                                  <small className="d-block text-muted">
                                    Components: {salaryDetailsForEmp.length}
                                  </small>
                                </div>
                              </td>
                              <td className="text-end pe-4">
                                <div className="btn-group" role="group">
                                  {/* NEW: View Calculations Button for Individual Employee */}
                                  <button
                                    className="btn btn-outline-info btn-sm me-1"
                                    onClick={() => viewEmployeeCalculations(employee)}
                                    title="View Salary Calculation Details"
                                  >
                                    <i className="bi bi-eye"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-success btn-sm me-1"
                                    onClick={() => handleCloneSalary(employee)}
                                    title="Clone Salary to Others"
                                  >
                                    <i className="bi bi-copy"></i>
                                  </button>
                                  {canAddSalaryDetail() && (
                                    <button
                                      className="btn btn-outline-primary btn-sm me-1"
                                      onClick={() => handleAdd(employee)}
                                      title="Add Salary Component"
                                    >
                                      <i className="bi bi-plus"></i>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>

                            {/* Salary Details Row - Expanded View */}
                            {salaryDetailsForEmp.length > 0 && (
                              <tr className="bg-light">
                                <td colSpan="5" className="p-0">
                                  <div className="p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <h6 className="fw-bold text-primary mb-0">
                                        <i className="bi bi-list-check me-2"></i>
                                        Salary Components ({salaryDetailsForEmp.length})
                                      </h6>
                                      <small className="text-muted">
                                        Employee ID: {employee.emp_id}
                                      </small>
                                    </div>
                                    <div className="table-responsive">
                                      <table className="table table-sm">
                                        <thead>
                                          <tr>
                                            <th>Component</th>
                                            <th>Type</th>
                                            <th>Calculation</th>
                                            <th>Value</th>
                                            <th>Order</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {salaryDetailsForEmp.map((detail) => (
                                            <tr key={detail.salary_detail_id}>
                                              <td>
                                                <div className="d-flex align-items-center">
                                                  <div className={`badge ${detail.component_type === 'Earning' || detail.component_type === 'Earing'
                                                    ? 'bg-success'
                                                    : 'bg-danger'
                                                    } me-2`}>
                                                    {detail.component_type === 'Earning' || detail.component_type === 'Earing' ? '+' : '-'}
                                                  </div>
                                                  {getComponentName(detail.component_id)}
                                                </div>
                                              </td>
                                              <td>
                                                <span className={`badge ${detail.component_type === 'Earning' || detail.component_type === 'Earing'
                                                  ? 'bg-success'
                                                  : 'bg-danger'
                                                  }`}>
                                                  {detail.component_type}
                                                </span>
                                              </td>
                                              <td>
                                                <span className="badge bg-info">
                                                  {detail.calculation_type}
                                                </span>
                                              </td>
                                              <td>
                                                <code>
                                                  {detail.calculation_type === 'Fixed' ? '₹' : ''}
                                                  {detail.value}
                                                  {detail.calculation_type === 'Percentage' ? '%' : ''}
                                                </code>
                                              </td>
                                              <td>
                                                <span className="badge bg-secondary">{detail.component_order}</span>
                                              </td>
                                              <td>
                                                <span className={`badge ${detail.is_active === 1 ? 'bg-success' : 'bg-secondary'
                                                  }`}>
                                                  {detail.is_active === 1 ? 'Active' : 'Inactive'}
                                                </span>
                                              </td>
                                              <td>
                                                <div className="btn-group btn-group-sm" role="group">
                                                  {canEditSalaryDetail() && (
                                                    <button
                                                      className="btn btn-outline-primary btn-sm me-1"
                                                      onClick={() => handleEdit(detail, employee)}
                                                      title="Edit Component"
                                                    >
                                                      <i className="bi bi-pencil"></i>
                                                    </button>
                                                  )}
                                                  {canDeleteSalaryDetail() && (
                                                    <button
                                                      className="btn btn-outline-danger btn-sm"
                                                      onClick={() => handleDelete(detail.salary_detail_id, employee.emp_id)}
                                                      title="Delete Component"
                                                    >
                                                      <i className="bi bi-trash"></i>
                                                    </button>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-3">
                  <div className="fw-bold text-primary fs-4">{employees.length}</div>
                  <small className="text-muted">Total Employees</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-success fs-4">
                    {employees.filter(e => e.status === 'Active').length}
                  </div>
                  <small className="text-muted">Active Employees</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-info fs-4">
                    {salaryDetails.length}
                  </div>
                  <small className="text-muted">Total Salary Components</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-warning fs-4">
                    {Object.keys(salarySummaries).length}
                  </div>
                  <small className="text-muted">Calculated Salaries</small>
                </div>
              </div>
              {/* Salary Summary Stats */}
              <div className="row text-center mt-3 pt-3 border-top">
                <div className="col-md-4">
                  <div className="fw-bold text-success fs-5">
                    ₹{Object.values(salarySummaries).reduce((sum, s) => sum + parseFloat(s.earnings || 0), 0).toFixed(2)}
                  </div>
                  <small className="text-muted">Total Monthly Earnings</small>
                </div>
                <div className="col-md-4">
                  <div className="fw-bold text-danger fs-5">
                    ₹{Object.values(salarySummaries).reduce((sum, s) => sum + parseFloat(s.deductions || 0), 0).toFixed(2)}
                  </div>
                  <small className="text-muted">Total Monthly Deductions</small>
                </div>
                <div className="col-md-4">
                  <div className="fw-bold text-primary fs-5">
                    ₹{Object.values(salarySummaries).reduce((sum, s) => sum + parseFloat(s.net || 0), 0).toFixed(2)}
                  </div>
                  <small className="text-muted">Total Monthly Net Salary</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card border-0">
            <div className="card-body text-center py-3">
              <div className="btn-group" role="group">
                <button
                  className="btn btn-outline-primary"
                  onClick={fetchSalaryDetails}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Refresh Data
                </button>
                <button
                  className="btn btn-outline-success"
                  onClick={() => navigate('/employees')}
                >
                  <i className="bi bi-people me-2"></i>
                  Manage Employees
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={() => navigate('/salary-templates')}
                >
                  <i className="bi bi-file-earmark-text me-2"></i>
                  Salary Templates
                </button>
                <button
                  className="btn btn-outline-warning"
                  onClick={refreshSalarySummaries}
                  disabled={calculatingSalaries || loadingCalculations}
                >
                  {calculatingSalaries ? (
                    <>
                      <i className="bi bi-arrow-clockwise spinner-border spinner-border-sm me-2"></i>
                      Calculating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-calculator me-2"></i>
                      Recalculate All Salaries
                    </>
                  )}
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={fetchDetailedCalculations}
                  disabled={loadingCalculations || calculatingSalaries || employees.length === 0}
                >
                  {loadingCalculations ? (
                    <>
                      <i className="bi bi-arrow-clockwise spinner-border spinner-border-sm me-2"></i>
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-calculator me-2"></i>
                      View All Calculations
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Salary Calculations Modal */}
      {showCalculationsModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="bi bi-calculator me-2"></i>
                  Salary Calculation Details
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowCalculationsModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                {loadingCalculations ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading calculation details...</p>
                  </div>
                ) : (
                  <div className="salary-calculations-container">
                    {Object.keys(detailedCalculations).length === 0 ? (
                      <div className="text-center py-5">
                        <i className="bi bi-calculator display-1 text-muted"></i>
                        <h4 className="mt-3 text-muted">No Calculation Data</h4>
                        <p className="text-muted">No detailed calculation data available.</p>
                      </div>
                    ) : (
                      Object.entries(detailedCalculations).map(([empId, data]) => {
                        const employee = employees.find(emp => emp.emp_id === parseInt(empId)) || {};
                        const summary = data.summary || {};
                        const components = data.components || [];

                        return (
                          <div key={empId} className="employee-calculation-card mb-4 border rounded p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                              <div>
                                <h6 className="fw-bold mb-1">
                                  {employee.first_name} {employee.last_name}
                                </h6>
                                <small className="text-muted">
                                  ID: {empId} | Code: {employee.emp_code} | Status:
                                  <span className={`badge ms-1 ${employee.status === 'Active' ? 'bg-success' :
                                    employee.status === 'Inactive' ? 'bg-secondary' : 'bg-danger'}`}>
                                    {employee.status || 'Unknown'}
                                  </span>
                                </small>
                              </div>
                              <div className="text-end">
                                <div className="fw-bold text-primary fs-5">₹{summary.net || '0.00'}</div>
                                <small className="text-muted">Monthly Net Salary</small>
                              </div>
                            </div>

                            {/* Summary Stats */}
                            <div className="row mb-3">
                              <div className="col-md-4">
                                <div className="card bg-light border-0">
                                  <div className="card-body text-center py-2">
                                    <div className="fw-bold text-success fs-6">₹{summary.earnings || '0.00'}</div>
                                    <small className="text-muted">Total Earnings</small>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="card bg-light border-0">
                                  <div className="card-body text-center py-2">
                                    <div className="fw-bold text-danger fs-6">₹{summary.deductions || '0.00'}</div>
                                    <small className="text-muted">Total Deductions</small>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="card bg-light border-0">
                                  <div className="card-body text-center py-2">
                                    <div className="fw-bold text-primary fs-6">₹{summary.net || '0.00'}</div>
                                    <small className="text-muted">Net Salary</small>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Components Breakdown */}
                            {components.length > 0 ? (
                              <>
                                <h6 className="fw-bold mb-2">Component Breakdown:</h6>
                                <div className="table-responsive">
                                  <table className="table table-sm table-hover">
                                    <thead className="bg-light">
                                      <tr>
                                        <th>Component</th>
                                        <th>Type</th>
                                        <th>Calculation</th>
                                        <th>Value</th>
                                        <th className="text-end">Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {components.map((comp, index) => (
                                        <tr key={index}>
                                          <td>
                                            <div className="d-flex align-items-center">
                                              <div className={`badge ${comp.type === 'Earning' || comp.type === 'Earing'
                                                ? 'bg-success'
                                                : comp.type === 'Deduction' ? 'bg-danger' : 'bg-secondary'
                                                } me-2`}>
                                                {comp.type === 'Earning' || comp.type === 'Earing' ? '+' :
                                                  comp.type === 'Deduction' ? '-' : '○'}
                                              </div>
                                              <span>{comp.name}</span>
                                            </div>
                                          </td>
                                          <td>
                                            <span className={`badge ${comp.type === 'Earning' || comp.type === 'Earing'
                                              ? 'bg-success'
                                              : comp.type === 'Deduction' ? 'bg-danger' : 'bg-secondary'
                                              }`}>
                                              {comp.type}
                                            </span>
                                          </td>
                                          <td>
                                            <span className="badge bg-info">{comp.calculation_type}</span>
                                          </td>
                                          <td>
                                            <code>
                                              {comp.calculation_type === 'Fixed' ? '₹' : ''}
                                              {comp.value}
                                              {comp.calculation_type === 'Percentage' ? '%' : ''}
                                              {comp.calculation_type === 'Formula' ? ' (Formula)' : ''}
                                              {comp.calculation_type === 'Variable' ? ' (Variable)' : ''}
                                            </code>
                                          </td>
                                          <td className="text-end fw-bold">
                                            ₹{parseFloat(comp.amount || 0).toFixed(2)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot className="bg-light">
                                      <tr>
                                        <td colSpan="4" className="text-end fw-bold">Total Earnings:</td>
                                        <td className="text-end fw-bold text-success">₹{summary.earnings || '0.00'}</td>
                                      </tr>
                                      <tr>
                                        <td colSpan="4" className="text-end fw-bold">Total Deductions:</td>
                                        <td className="text-end fw-bold text-danger">₹{summary.deductions || '0.00'}</td>
                                      </tr>
                                      <tr>
                                        <td colSpan="4" className="text-end fw-bold">Net Salary:</td>
                                        <td className="text-end fw-bold text-primary">₹{summary.net || '0.00'}</td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </>
                            ) : (
                              <div className="alert alert-info">
                                <i className="bi bi-info-circle me-2"></i>
                                No detailed component breakdown available.
                              </div>
                            )}

                            {data.error && (
                              <div className="alert alert-warning mt-2">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                {data.error}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCalculationsModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowCalculationsModal(false);
                    refreshSalarySummaries();
                  }}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Recalculate All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAddModal && selectedEmployee && canAddSalaryDetail() && (
        <AddSalaryDetail
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          employee={selectedEmployee}
          payComponents={payComponents}
          onSalaryDetailAdded={async () => {
            await fetchSalaryDetails();
            await calculateSingleEmployeeSalary(selectedEmployee.emp_id);
          }}
        />
      )}

      {showEditModal && selectedSalaryDetail && selectedEmployee && canEditSalaryDetail() && (
        <EditSalaryDetail
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          salaryDetail={selectedSalaryDetail}
          employee={selectedEmployee}
          payComponents={payComponents}
          onSalaryDetailUpdated={async () => {
            await fetchSalaryDetails();
            await calculateSingleEmployeeSalary(selectedEmployee.emp_id);
          }}
        />
      )}

      {showImportModal && (
        <ImportSalaryDetails
          show={showImportModal}
          onHide={() => setShowImportModal(false)}
          onImport={handleImport}
          canImportSalary={canImportSalaryDetails()}
          error={error}
          setError={setError}
          API_BASE={API_BASE}
        />
      )}

      <style jsx>{`
        .employee-row:hover {
          background-color: rgba(0, 123, 255, 0.05);
        }
        
        .employee-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .debug-panel {
          font-family: 'Courier New', monospace;
          background: #f8f9fa;
          border-left: 4px solid #ffc107;
        }
        
        .salary-calculations-container {
          max-height: 70vh;
          overflow-y: auto;
        }
        
        .employee-calculation-card {
          background-color: #f8f9fa;
          border-color: #dee2e6 !important;
        }
        
        .employee-calculation-card:hover {
          background-color: #e9ecef;
        }
        
        .modal-backdrop {
          opacity: 0.5 !important;
        }
      `}</style>
    </div>
  );
};

export default SalaryDetails;